<template>
  <div class="editor-header-grid border-b border-gray-300 py-2 px-4" data-testid="editor-header">
    <div v-if="!isEditMode || isGridView" class="flex gap-2 xl1_gap-4 items-end">
      <EditorDeckCreateSlideButton
        button-class="create-header-button"
        :is-book-meeting-enabled="isBookMeetingEnabled"
        @create-slide="$emit('create-slide', $event)"
        @import-slides="$emit('import-slides')"
      />
      <div>
        <VButton
          class="header-button"
          data-testid="create-section-header-btn"
          data-intercom-target="add-section-header-button"
          variant="copper-borderless"
          no-shadow="noShadow"
          :disabled="isGridView"
          @click="$emit('create-header')"
        >
          <FontAwesomeIcon icon="heading" size="2x" transform="shrink-2" :class="['w-8', { disabled: isGridView }]" /><span
            class="xl1_hidden"
          >
            Header
          </span>
          <span class="hidden xl1_inline">Section Header</span>
        </VButton>
      </div>
      <EditorDeckToggleViewButton
        v-if="hasSlides"
        button-class="toggle-view-button"
        :is-grid-view="isGridView"
        @toggle-view-mode="$emit('toggle-view-mode')"
      />
    </div>
    <PortalTarget v-else name="deck-editor-header-controls" />

    <div class="flex justify-center items-center gap-1 min-w-0 col-start-2 mx-2">
      <VCircleLogo class="w-6 h-6 text-gray-900 flex-shrink-0" />
      <h1 v-if="isEditMode && !isGridView" class="header-title">
        <slot name="slide-title" />
      </h1>
      <div v-else class="flex items-center min-w-0">
        <InlineEditable
          :value="title"
          class="copper-light text-gray-800 truncate"
          placeholder="Deck title"
          data-testid="deck-title"
          theme="light"
          @update:value="title = $event"
          @click.prevent
          @save="$emit('update:deck-title', title.trim())"
          @clear="title = deck.title"
          @cancel="title = deck.title"
        />
        <FlyoutMenu data-testid="deck-editor-header-menu" menu-class="deck-management-menu w-88 -left-36 text-sm font-normal">
          <div v-if="deck.view_access_type === 'selected' || deck.view_access_type === 'private'">
            <li>
              <button type="button" data-testid="access-btn" class="deck-management-btn" @click="$emit('open-secure-access')">
                <div class="icon"><FontAwesomeIcon icon="lock" size="sm" /></div>
                Manage Access
              </button>
            </li>
          </div>
          <div>
            <li>
              <button type="button" class="deck-management-btn" @click="$emit('duplicate-deck')">
                <div class="icon"><FontAwesomeIcon icon="clone" size="sm" /></div>
                Duplicate Deck
              </button>
            </li>
            <li v-if="isInternalAdmin">
              <button type="button" class="deck-management-btn gold" @click="$emit('duplicate-deck-different-company')">
                <div class="icon"><FontAwesomeIcon icon="clone" size="sm" /></div>
                Duplicate Deck to a Different Company
              </button>
            </li>
          </div>
          <div>
            <li>
              <button type="button" data-testid="deck-embed" class="deck-management-btn" @click="goToEmbeds">
                <div class="icon"><FontAwesomeIcon icon="code" size="sm" /></div>
                Embed Deck
              </button>
            </li>
          </div>
          <div>
            <li>
              <button type="button" class="deck-management-btn" @click.prevent.stop="$emit('archive-deck')">
                <div class="icon"><FontAwesomeIcon icon="box-archive" /></div>
                <template v-if="!deck.archived">Archive Deck</template>
                <template v-else>Unarchive Deck</template>
              </button>
            </li>
            <li>
              <button type="button" class="deck-management-btn" @click="$emit('delete-deck')">
                <div class="icon"><FontAwesomeIcon icon="trash" size="sm" /></div>
                Delete Deck
              </button>
            </li>
          </div>
        </FlyoutMenu>
      </div>
    </div>
    <div v-if="!isEditMode || isGridView" class="flex justify-end gap-2 xl1_gap-4">
      <VButton
        class="header-button"
        variant="copper-borderless"
        no-shadow="noShadow"
        data-intercom-target="deck-settings-button"
        @click="$emit('open-deck-settings')"
      >
        <CogIcon class="h-5 w-5" /><span class="xl1_hidden">Settings</span><span class="hidden xl1_inline">Deck Settings</span>
      </VButton>
      <VButton class="header-button" data-testid="deck-record" variant="copper-borderless" no-shadow="noShadow" @click="startRecord">
        <Record class="h-5 w-5" /><span class="xl1_hidden">Record</span><span class="hidden xl1_inline">Record</span>
      </VButton>
      <VButton
        class="header-button"
        data-testid="deck-preview"
        variant="copper-borderless"
        no-shadow="noShadow"
        data-intercom-target="preview-button"
        @click="goToDeckPreview"
      >
        <EyeIcon class="h-5 w-5" />Preview
      </VButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { PortalTarget } from 'portal-vue'
import type { DeepReadonly, PropType } from 'vue'
import { EyeIcon, CogIcon } from '@heroicons/vue/20/solid'
import VCircleLogo from '@/components/icons/VCircleLogo.svg'
import Record from '@/components/icons/Record.svg'
import type { EditDeck } from '~/composables/useEditDeck'
import { generateDeckTitle } from '~/composables/useDeck'

const props = defineProps({
  deck: {
    type: Object as PropType<DeepReadonly<EditDeck>>,
    required: true,
  },
  isBookMeetingEnabled: {
    type: Boolean,
    default: false,
  },
  isEditMode: {
    type: Boolean,
    default: false,
  },
  isGridView: {
    type: Boolean,
    default: false,
  },
  isInternalAdmin: {
    type: Boolean,
    default: false,
  },
  hasSlides: {
    type: Boolean,
    default: false,
  },
  selectedSlideIds: {
    type: Array as PropType<number[]>,
    default: () => [],
  },
})

defineEmits([
  'create-slide',
  'toggle-view-mode',
  'create-header',
  'import-slides',
  'open-deck-settings',
  'archive-deck',
  'delete-deck',
  'open-secure-access',
  'duplicate-deck',
  'duplicate-deck-different-company',
  'update:deck-title',
])

const title = ref(props.deck.title)

const router = useRouter()
const { $analytics, $sentry } = useNuxtApp()
const { state: userState, setSelectedCompany } = useUser()
const { createRecording } = useMeetings({ userState, setSelectedCompany })

const { goToEmbeds } = useEmbedNavigation({ deck: props.deck })

function goToDeckPreview() {
  let query: Record<string, string> = {}
  if (props.selectedSlideIds.length === 1) {
    query.slide = `${props.selectedSlideIds[0]}`
  }

  const route = router.resolve({
    name: 'decks-id',
    params: { id: `${props.deck.id}` },
    query: query,
  })
  window.open(route.href, '_blank')
}

async function startRecord() {
  try {
    const { share_id } = await createRecording({
      title: generateDeckTitle(props.deck),
      deckId: props.deck.id,
      companyId: props.deck.company_id,
    })

    $analytics.track('DeckEditorClickCreateVideoRecording', {
      companyName: props.deck.company.name,
      companyId: props.deck.company_id,
      deckName: props.deck.title,
      deckId: props.deck.id,
    })

    navigateTo({ name: 'recordings-id', params: { id: share_id }, query: { from: window.location.href } })
  } catch (e) {
    throw e
  }
}

watch(
  () => props.deck,
  () => (title.value = props.deck.title),
)
</script>

<style lang="stylus" scoped>
:deep(.header-button), :deep(.create-header-button), :deep(.toggle-view-button)
  @apply relative flex flex-col justify-between items-center text-xs leading-5 font-normal whitespace-nowrap px-2 gap-1 uppercase xl1_normal-case

  svg
    @apply flex-shrink-0 text-copper-600
    &.disabled
      @apply text-gray-400


:deep(.create-header-button)
  svg
    @apply w-8 h-8 -m-1

:deep(.floating-slide-list)
  @apply absolute z-10 left-1/4
  top: calc(100% + 0.75rem)
  --arrowLeft: 2rem

  .slide-list
    @apply w-64 rounded-2xl overflow-hidden divide-y divide-gray-300

    .slide-list-button
      @apply w-full flex gap-2 px-6 py-3 cursor-pointer rounded-none

      &:hover
        @apply bg-copper-100

      svg
        @apply w-5 h-5 text-copper-600

      span
        @apply text-sm leading-5 font-normal

.header-title
  @apply flex items-center text-gray-800 font-normal my-0 ml-2 text-base
  height: 72px

:deep(.deck-management-menu)
  & > *
    @apply border-gray-200 py-1
  & > * + *
    @apply border-t

:deep(.deck-management-btn)
  @apply flex gap-2
  @apply text-sm appearance-none border-none bg-transparent px-4 py-2 w-full text-left text-gray-900
  .icon
    @apply w-4 text-center
  &:hover
    @apply bg-copper-100 text-copper-900
  &.gold
    @apply text-gold-900
    &:hover
      @apply bg-gold-200
</style>
